import { findRecord } from '@warp-drive/utilities/json-api';
import { warn } from '@ember/debug';

import type { Store } from '@warp-drive/core';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';

// PaperTrail whodunnit fields only hold a user FK for authenticated changes;
// system/seed/import actions leave a non-numeric string (e.g. "demo-user") or
// a JSONB object — neither resolves to GET /users/:id.
export function isResolvableUserId(value: unknown): value is string | number {
  if (typeof value === 'number') return Number.isInteger(value);
  return typeof value === 'string' && /^\d+$/.test(value);
}

// Non-numeric values return as-is with NO request issued — a GET
// /users/demo-user would 404 and surface as an unhandled promise rejection
// under WarpDrive.
export async function resolveAuditUserName(store: Store, value: unknown): Promise<string> {
  if (!isResolvableUserId(value)) {
    return typeof value === 'string' ? value : '';
  }

  try {
    const { content } = (await store.request(
      findRecord<UserModel>('user', String(value), { include: 'person' })
    )) as { content: { data: UserModel } };
    const person = content.data?.person;
    return person ? `${person.firstName} ${person.lastName}` : '';
  } catch (error) {
    // Only a 404 (since-deleted user) falls back to the raw id; other failures
    // (5xx, connectivity) are real errors we must not silently mask as a name.
    if ((error as { status?: number })?.status === 404) {
      return String(value);
    }
    warn(
      `resolve-audit-user: failed to resolve user "${String(value)}" ` +
        `(status ${(error as { status?: number })?.status ?? 'unknown'})`,
      false,
      { id: 'tio-common.resolve-audit-user.request-failed' }
    );
    return '';
  }
}
